'use client'

import { AddAttachmentIcon } from '@repo/ui/AddAttachmentIcon'
import { DOCIcon } from '@repo/ui/DOCIcon'
import { FileIcon } from '@repo/ui/FileIcon'
import { formatBytes } from '@repo/utils/numbers'
import { formatDate } from '@repo/utils/time'
import { HoverActionsPill } from '@repo/ui/HoverActionsPill'
import { JPEGIcon } from '@repo/ui/JPEGIcon'
import { LoadingState } from '@repo/ui/LoadingState'
import { MP4Icon } from '@repo/ui/MP4Icon'
import { MSGIcon } from '@repo/ui/MSGIcon'
import { OtherIcon } from '@repo/ui/OtherIcon'
import { PDFIcon } from '@repo/ui/PDFIcon'
import { PPTIcon } from '@repo/ui/PPTIcon'
import { useDebounce } from '@uidotdev/usehooks'
import { useDocumentBundleWizardStore } from '@repo/store/useDocumentBundleWizardStore'
import { useEffect, useRef, useState, type UIEvent } from 'react'
import { XLSIcon } from '@repo/ui/XLSIcon'
import { ZIPIcon } from '@repo/ui/ZIPIcon'
import MUIButton from '@mui/material/Button'
import MUIInput from '@mui/material/Input'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'

type DocumentBundleMatterAttachmentsCardProps = {
  clientMatterNo?: string | null
  sectionId?: number | string | null
}
export const DocumentBundleMatterAttachmentsCard = ({
  clientMatterNo,
  sectionId
}: DocumentBundleMatterAttachmentsCardProps) => {
  const [files, setFiles] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [pageSize] = useState(25)
  const [searchTerm, setSearchTerm] = useState('')
  const [startAtIndex, setStartAtIndex] = useState(0)
  const [totalRowCount, setTotalRowCount] = useState(0)
  const [addingIds, setAddingIds] = useState<Set<number | string>>(new Set())

  const debouncedSearchTerm = useDebounce(searchTerm, 500)
  const debouncedStartAtIndex = useDebounce(startAtIndex, 500)

  const hasTriggeredFetchRef = useRef<boolean>(false)
  const isFetchingRef = useRef<boolean>(false)
  const listRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  const { fetchMatterAttachments, fetchAttachmentContent, addNewDocumentsToSection } = useDocumentBundleWizardStore()

  useEffect(() => {
    const fetchData = async () => {
      if (isFetchingRef.current && debouncedStartAtIndex > 0) return

      try {
        setLoading(true)
        isFetchingRef.current = true

        const res = await fetchMatterAttachments({
          clientMatterNo,
          startAtIndex: debouncedStartAtIndex,
          searchTerm: debouncedSearchTerm,
          pageSize
        })

        if (debouncedStartAtIndex > 0) {
          setFiles((prevRows) => [...prevRows, ...(res?.rows || [])])
        } else {
          setFiles(res?.rows || [])
        }

        setTotalRowCount(res?.totalRowCount || 0)
      } catch (err) {
        console.error('Error fetching matter attachments:', err)
        setTotalRowCount(0)
      } finally {
        setLoading(false)
        isFetchingRef.current = false
      }
    }

    fetchData()
  }, [debouncedSearchTerm, debouncedStartAtIndex, clientMatterNo, fetchMatterAttachments, pageSize])

  useEffect(() => {
    // reset when the matter changes
    setStartAtIndex(0)
    setTotalRowCount(0)
    setFiles([])
  }, [clientMatterNo])

  const handleSearch = async () => {
    if (!searchTerm) return

    try {
      setLoading(true)
      setStartAtIndex(0)
      listRef.current?.scrollTo({ top: 0, behavior: 'smooth' })

      const res = await fetchMatterAttachments({
        clientMatterNo,
        searchTerm: debouncedSearchTerm,
        startAtIndex: debouncedStartAtIndex,
        pageSize
      })

      setTotalRowCount(res?.totalRowCount || 0)
    } catch (err) {
      console.error('Error searching matter attachments:', err)
      setTotalRowCount(0)
    } finally {
      setLoading(false)
    }
  }

  const handleAddAttachment = async (file: any) => {
    if (!sectionId || addingIds.has(file.matterHistoryContentId)) return

    setAddingIds((prev) => new Set(prev).add(file.matterHistoryContentId))
    try {
      const blob = await fetchAttachmentContent({ matterHistoryContentId: file.matterHistoryContentId })
      if (!blob) return

      const attachmentFile = new File([blob], file.filename, { type: blob.type })
      addNewDocumentsToSection({ sectionId, files: [attachmentFile] })
    } catch (err) {
      console.error('Error adding attachment to section:', err)
    } finally {
      setAddingIds((prev) => {
        const next = new Set(prev)
        next.delete(file.matterHistoryContentId)
        return next
      })
    }
  }

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1

    if (isAtBottom) {
      if (!hasTriggeredFetchRef.current && files.length < totalRowCount && !loading && !isFetchingRef.current) {
        hasTriggeredFetchRef.current = true
        setStartAtIndex((prev) => prev + pageSize)
      }
    } else {
      hasTriggeredFetchRef.current = false
    }
  }

  const renderIcon = (fileExtension: string) => {
    const sx = { height: '32px', width: '32px', mr: 1 }

    switch (fileExtension) {
      case 'doc':
      case 'docx':
      case 'txt':
      case 'rtf':
        return <DOCIcon sx={sx} />
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'tif':
      case 'tiff':
      case 'bmp':
      case 'webp':
      case 'heic':
      case 'heif':
      case 'heif-sequence':
      case 'heic-sequence':
        return <JPEGIcon sx={sx} />
      case 'xls':
      case 'xlsx':
      case 'csv':
        return <XLSIcon sx={sx} />
      case 'ppt':
      case 'pptx':
        return <PPTIcon sx={sx} />
      case 'avi':
      case 'm4v':
      case 'mov':
      case 'mp4':
      case 'mpeg':
      case 'mpg':
      case 'webm':
      case 'wmv':
        return <MP4Icon sx={sx} />
      case 'msg':
        return <MSGIcon sx={sx} />
      case 'pdf':
        return <PDFIcon sx={sx} />
      case 'zip':
        return <ZIPIcon sx={sx} />
      default:
        return <OtherIcon sx={sx} />
    }
  }

  return (
    <MUIStack direction="column" spacing={2} sx={{ pb: 4 }} data-testid="DocumentBundleMatterAttachmentsCard">
      <MUIStack direction="row" spacing={1} sx={{ mb: 2 }}>
        <MUIInput
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
            handleSearch()
          }}
          disabled={!clientMatterNo}
          onKeyUp={(e) => e.key === 'Enter' && handleSearch()}
          inputProps={{ ref: searchInputRef, maxLength: 80 }}
          sx={{
            width: '100%',
            input: {
              backgroundColor: 'background.dark',
              height: '40px',
              padding: '0 0 0 14px',
              borderRadius: '6px'
            },
            '&:before, &:after': { content: 'none' }
          }}
        />
      </MUIStack>

      <MUIStack
        direction="column"
        spacing={4}
        ref={listRef}
        sx={{ maxHeight: '300px', minHeight: '18.75rem', overflowY: 'auto', pr: 1, pt: 4 }}
        onScroll={handleScroll}
      >
        {files && files.length > 0
          ? files.map((file: any) => (
              <MUIStack
                key={file.matterHistoryContentId}
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={{
                  width: '100%',
                  maxHeight: '5rem',
                  position: 'relative',
                  '&:hover': { '.hoverActionsPill': { visibility: 'visible', opacity: 1 } }
                }}
              >
                {renderIcon(file.fileExtension)}

                <MUIStack direction="column" spacing={0.5} sx={{ overflow: 'hidden', width: '100%' }}>
                  <MUIStack
                    direction="row"
                    spacing={2}
                    alignItems="flex-start"
                    sx={{ overflow: 'hidden', width: '100%' }}
                  >
                    <MUITypography variant="body2" sx={{ textWrap: 'nowrap', textTransform: 'none' }}>
                      {file.filename}
                    </MUITypography>
                  </MUIStack>
                  <MUIStack direction="row" spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
                    <MUITypography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {formatBytes(file.size)}
                    </MUITypography>
                    <MUITypography variant="body2" color="text.secondary" sx={{ margin: '0 8px' }}>
                      |
                    </MUITypography>
                    <MUITypography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textTransform: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {formatDate(file.createdDate)}
                    </MUITypography>
                  </MUIStack>
                </MUIStack>

                {sectionId &&
                  (addingIds.has(file.matterHistoryContentId) ? (
                    <LoadingState sx={{ height: '18px', width: '18px' }} />
                  ) : (
                    <HoverActionsPill top="0px" padding="4px" height="38px">
                      <MUITooltip title="Add attachment" arrow placement="top">
                        <MUIButton
                          disabled={addingIds.has(file.matterHistoryContentId)}
                          onClick={() => handleAddAttachment(file)}
                          sx={{ minWidth: '30px', borderRadius: '50%' }}
                        >
                          <AddAttachmentIcon sx={{ height: '20px', width: '20px' }} />
                        </MUIButton>
                      </MUITooltip>
                    </HoverActionsPill>
                  ))}
              </MUIStack>
            ))
          : null}

        {files && files.length === 0 && !loading ? (
          <MUIStack direction="column" alignItems="center" justifyContent="center" sx={{ py: 4, minHeight: '16rem' }}>
            <FileIcon sx={{ width: '3rem', height: '3rem', mt: 0 }} />
            <MUITypography variant="body1" sx={{ textAlign: 'center', mt: 2 }}>
              No attachments found.
            </MUITypography>
          </MUIStack>
        ) : null}

        {loading ? (
          <MUIStack
            direction="row"
            justifyContent="center"
            alignItems="center"
            sx={{ height: '100%', width: '100%', minHeight: '16rem' }}
          >
            <LoadingState />
          </MUIStack>
        ) : null}
      </MUIStack>
    </MUIStack>
  )
}
